import { CommonModule } from '@angular/common';
import { Component, OnInit, Signal, signal } from '@angular/core';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { GetCandidate } from 'src/app/models/candidate/get-candidate';
import { GetResumeDetail, ResumeEmployment } from 'src/app/models/resume/resume';
import { CandidateService } from 'src/app/services/candidate.service';
import { ResumeService } from 'src/app/services/resume.service';
import { AuthService } from 'src/app/services/auth.service';
import { environment } from 'src/environments/environment';
import { Paged } from 'src/app/models/shared/paged';
import { GetFreelancerFilter, GetUserFilter } from 'src/app/models/security/get-user-filter';
import { ViewTrackerDirective } from 'src/app/directives/view-tracker.directive';
import { ViewType } from 'src/app/services/view-tracking.service';

@Component({
    selector: 'app-freelancer-detail',
    templateUrl: './freelancer-detail.component.html',
    styles: [],
    standalone: true,
    imports: [ RouterLink, CommonModule, ViewTrackerDirective]
})
export class FreelancerDetailComponent implements OnInit {
  profesionals: any[] = [];
  otherProfessionalsLoading = false;

  apiUrl = environment.api.url;
  candidateId: number = 0;
  resumeId: number = 0;
  isLoading: boolean = true;
  hasError: boolean = false;
  errorMessage: string = '';
  
  professionalData = signal<GetCandidate | null>(null);
  resumeData = signal<GetResumeDetail | null>(null);
  ViewType = ViewType;
  recruiterId: number | null = null;
  
  // Getter to obtain candidate's publicResumeId for unified tracking
  // Prioritizes publicResumeId, then professionalProfileId, finally URL resumeId
  get trackingResumeId(): number {
    const candidate = this.professionalData();
    return candidate?.publicResumeId || candidate?.professionalProfileId || this.resumeId;
  }
  
  constructor(
    private route: ActivatedRoute,
    private candidateService: CandidateService,
    private resumeService: ResumeService,
    private authService: AuthService
  ) { }

  ngOnInit(): void {
    this.recruiterId = this.authService.getUserId();
    this.route.params.subscribe(params => {
      if (params['userId'] && params['resumeId']) {
        this.candidateId = +params['userId'];
        this.resumeId = +params['resumeId'];
        this.loadProfessionalData();
        this.loadOtherProfessionals();
      } else {
        this.hasError = true;
        this.errorMessage = 'Información del profesional no encontrada';
        this.isLoading = false;
      }
    });
  }
  
  loadProfessionalData(): void {
    this.isLoading = true;
    this.hasError = false;
    
    this.candidateService.getIndependentProfessionalsById(this.candidateId).subscribe({
      next: (candidateData) => {
        this.professionalData.set(candidateData);
        this.loadResumeData();
      },
      error: (error) => {
        console.error('Error al cargar datos del profesional:', error);
        this.hasError = true;
        this.errorMessage = 'No se pudo cargar la información del profesional';
        this.isLoading = false;
      }
    });
  }
  
  loadResumeData(): void {
    this.resumeService.getResumeDetail(this.resumeId).subscribe({
      next: (resumeData) => {
        this.resumeData.set(resumeData);
        this.isLoading = false;
      },
      error: (error) => {
        console.error('Error al cargar CV del profesional:', error);
        this.hasError = true;
        this.errorMessage = 'No se pudo cargar el CV del profesional';
        this.isLoading = false;
      }
    });
  }
  
  getPhotoUrl(): string {
    const professional = this.professionalData();
    if (professional && professional.photoFileId) {
      return `${this.apiUrl}/File/ViewFile/${professional.photoFileId}`;
    }
    return 'assets/Images/user-profile.png';
  }
  
  
  calculateAge(birthDate: string | null | undefined): number {
    if (!birthDate) return 0;
    
    const today = new Date();
    const birth = new Date(birthDate);
    let age = today.getFullYear() - birth.getFullYear();
    const monthDiff = today.getMonth() - birth.getMonth();
    
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
      age--;
    }
    
    return age;
  }

  calculateTotalExperience(): string {
    const resume = this.resumeData();
    if (!resume || !resume.employments || resume.employments.length === 0) {
      return 'Sin experiencia';
    }

    let totalMonths = 0;

    // Calculate total months of experience from all employments
    resume.employments.forEach(employment => {
      if (employment.startDate) {
        const startDate = new Date(employment.startDate);
        let endDate: Date;
        
        if (employment.currentJob) {
          // If current job, use today's date
          endDate = new Date();
        } else if (employment.endDate) {
          // If job ended, use end date
          endDate = new Date(employment.endDate);
        } else {
          // If no end date and not current, use start date (1 day of experience)
          endDate = new Date(startDate);
        }

        // Calculate months between dates
        const months = (endDate.getFullYear() - startDate.getFullYear()) * 12 + 
                      (endDate.getMonth() - startDate.getMonth());
        
        totalMonths += Math.max(1, months); // Minimum 1 month per employment
      }
    });

    // Format the experience nicely
    if (totalMonths === 0) {
      return 'Sin experiencia';
    } else if (totalMonths < 12) {
      return `${totalMonths} ${totalMonths === 1 ? 'mes' : 'meses'}`;
    } else {
      const years = Math.floor(totalMonths / 12);
      const remainingMonths = totalMonths % 12;
      
      if (years === 1) {
        if (remainingMonths === 0) {
          return '1 año';
        } else {
          return `1 año y ${remainingMonths} ${remainingMonths === 1 ? 'mes' : 'meses'}`;
        }
      } else {
        if (remainingMonths === 0) {
          return `${years} años`;
        } else {
          return `${years} años y ${remainingMonths} ${remainingMonths === 1 ? 'mes' : 'meses'}`;
        }
      }
    }
  }

  formatChargeInfo(): string {
    const resume = this.resumeData();
    if (!resume || !resume.amountToChargePerType || !resume.chargeType) {
      return 'Consultar tarifa';
    }
    
    return `RD$ ${resume.amountToChargePerType} x ${resume.chargeType}`;
  }

  loadOtherProfessionals(): void {
    this.otherProfessionalsLoading = true;
    
    const filter: GetFreelancerFilter = {
      page: 1,
      pageSize: 4,
      filterKey: null,
      filterValue: null,
      chargeTypeId: null,
      amountFrom: null,
      amountTo: null,
      professionalProfileId: null,
      stateId: null,
      departmentId: null,
      canTravel: null,
      hasVehicleInsurance: null,
      hasWorkPermit: null,
      isBilingual: null,
      hasCertifications: null
    };
    
    this.candidateService.getIndependentProfessionals(filter).subscribe({
      next: (response: Paged<GetCandidate>) => {
        // Filter out the current professional if present
        const filteredItems = response.items.filter(c => c.userId !== this.candidateId);
        
        this.profesionals = filteredItems.map((candidate: GetCandidate) => {
          let photoUrl = 'assets/Images/user.png';
          if (candidate.photoFileId) {
            photoUrl = `${this.apiUrl}/File/ViewFile/${candidate.photoFileId}`;
          }
          
          return {
            id: candidate.userId,
            resumeId: candidate.publicResumeId,
            foto: photoUrl,
            profesional: candidate.professionalProfileName || 'Profesional Independiente',
            nombreCompleto: `${candidate.firstName || ''} ${candidate.lastName || ''}`,
            experiencia: 'Profesional cualificado',
            price: candidate.publicResumeId ? 'Ver tarifas' : 'Consultar tarifa'
          };
        });
        
        this.otherProfessionalsLoading = false;
      },
      error: (error) => {
        console.error('Error al cargar otros profesionales:', error);
        this.otherProfessionalsLoading = false;
      }
    });
  }

}
